import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { motion, AnimatePresence } from 'framer-motion';
import config from '../../config';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Helper function to parse the complex response string
const parseWidgetData = (widgetData) => {
    if (!widgetData || widgetData.type !== 'chart') return null;

    try {
        // 1. Parse the outer content string
        let parsedContent = typeof widgetData.content === 'string' 
            ? JSON.parse(widgetData.content) 
            : widgetData.content;

        // 2. Handle double-nested "content" (If the parsed object has another 'content' key inside)
        if (parsedContent.content && parsedContent.content.config) {
            parsedContent = parsedContent.content;
        }

        let rawConfig = parsedContent.config;
        let chartConfig;

        // 3. Process the inner config string or object
        if (typeof rawConfig === 'string') {
            rawConfig = rawConfig.trim().replace(/,\s*$/, '');
            try {
                chartConfig = JSON.parse(rawConfig);
            } catch (jsonError) {
                console.warn("Strict JSON parsing failed, falling back to JS evaluation.");
                chartConfig = new Function(`return ${rawConfig}`)();
            }
        } else {
            chartConfig = rawConfig || {};
        }

        // Ensure safe defaults for rendering if data is missing
        if (!chartConfig.data) {
            chartConfig.data = { labels: [], datasets: [] };
        }
        
        // Ensure options exist to prevent the undefined error, as backend no longer sends them
        if (!chartConfig.options) {
            chartConfig.options = { responsive: true, maintainAspectRatio: false };
        }

        // 4. Generate table data from the chart config safely
        const datasets = chartConfig.data.datasets || [];
        const labels = chartConfig.data.labels || [];
        
        const tableData = {
            headers: ['Model', datasets[0]?.label || 'Value'],
            rows: labels.map((label, index) => [
                label,
                datasets[0]?.data?.[index] || 0
            ])
        };

        // 5. Return the formatted widget object
        return {
            id: widgetData.id,
            title: parsedContent.title || 'Data Visualization',
            config: chartConfig,
            tableData: tableData,
        };
    } catch (e) {
        console.error("Critical error parsing widget data:", e);
        return null;
    }
};


const CreateGraphModal = ({ isOpen, onClose, widgets, setWidgets, layout, setLayout }) => {
    const [prompt, setPrompt] = useState('');
    const [widgetPreview, setWidgetPreview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState('prompt'); // 'prompt', 'preview'

    // Reset state when modal is closed or reopened
    useEffect(() => {
        if (isOpen) {
            setStep('prompt');
            setWidgetPreview(null);
            setPrompt('');
        }
    }, [isOpen]);

    // Simulates calling an AI API to get chart data
    const handleGenerateWidget = () => {
        if (!prompt.trim()) return; // Don't generate if prompt is empty
        setIsLoading(true);
        setWidgetPreview(null);

        fetch(`${config.API_BASE_URL}/ai-data-provider/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ "prompt": prompt }),
        })
        .then(response => {
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            return response.json();
        })
        .then(apiResponse => {
            const responseWithId = {
                id: `widget-${Date.now()}`,
                ...apiResponse 
            };

            const parsedWidget = parseWidgetData(responseWithId);
            setWidgetPreview(parsedWidget);
            setStep('preview');
        })
        .catch(error => {
            console.error('There was a problem with the fetch operation:', error);
        })
        .finally(() => {
            setIsLoading(false);
        });
    };

    // Adds the accepted widget to the main dashboard
    const handleAcceptWidget = () => {
        if (!widgetPreview) return;

        const newLayoutItem = {
            i: widgetPreview.id,
            x: (layout.length * 6) % 12, 
            y: Infinity, 
            w: 6,
            h: 5, 
            minW: 4,
            minH: 4,
        };

        setWidgets([...widgets, widgetPreview]);
        setLayout([...layout, newLayoutItem]);
        handleClose();
    };
    
    // Reset state and close modal
    const handleClose = () => {
        onClose();
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.95 },
        visible: { opacity: 1, scale: 1 },
        exit: { opacity: 0, scale: 0.95 }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <motion.div
                        className="bg-white/90 w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden"
                        variants={modalVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                        <div className="p-8">
                            <h2 className="text-3xl font-bold text-center text-gray-800 mb-2">Create a New Insight</h2>
                            <p className="text-center text-gray-500 mb-8">Describe the data you want to visualize.</p>

                            {/* --- Prompt Input Area --- */}
                            <div className="flex items-center space-x-4 mb-8">
                                <textarea
                                    className="w-full px-4 py-3 text-gray-700 bg-white-100 border-2 border-transparent rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:bg-white transition"
                                    rows="2"
                                    placeholder="e.g., 'Show top 5 car models with the highest warranty claims this year'"
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    disabled={isLoading}
                                />
                                <motion.button
                                    onClick={handleGenerateWidget}
                                    className="px-6 py-4 bg-blue-500 text-white font-semibold rounded-lg shadow-lg disabled:bg-blue-300 disabled:cursor-not-allowed"
                                    disabled={isLoading}
                                    whileHover={{ scale: isLoading ? 1 : 1.05 }}
                                    whileTap={{ scale: isLoading ? 1 : 0.95 }}
                                >
                                    {isLoading ? 'Generating...' : 'Generate'}
                                </motion.button>
                            </div>

                            {/* --- Preview Section --- */}
                            <AnimatePresence>
                                {(isLoading || widgetPreview) && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        <div className="border-t-2 border-gray-200 pt-6">
                                            {isLoading && (
                                                <div className="flex flex-col items-center justify-center h-80">
                                                    <motion.div
                                                        className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full"
                                                        animate={{ rotate: 360 }}
                                                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                                    />
                                                    <p className="mt-4 text-gray-600">AI is analyzing your request...</p>
                                                </div>
                                            )}
                                            {step === 'preview' && widgetPreview && widgetPreview.config && (
                                                <div>
                                                    <h3 className="text-xl font-bold text-gray-800 mb-4 text-center">{widgetPreview.title}</h3>
                                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-[400px]">
                                                        {/* Chart Preview */}
                                                        <div className="bg-white/80 p-4 rounded-lg shadow-inner">
                                                            <Bar options={widgetPreview.config.options} data={widgetPreview.config.data} />
                                                        </div>
                                                        {/* Table Preview */}
                                                        <div className="bg-white/80 p-4 rounded-lg shadow-inner overflow-y-auto">
                                                            <table className="w-full text-left">
                                                                <thead className="sticky top-0 bg-gray-100">
                                                                    <tr>
                                                                        {widgetPreview.tableData.headers.map(header => (
                                                                            <th key={header} className="p-3 text-sm font-semibold text-gray-600">{header}</th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {widgetPreview.tableData.rows.map((row, index) => (
                                                                        <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                                                                            {row.map((cell, cellIndex) => (
                                                                                <td key={cellIndex} className="p-3 text-gray-700">{cell}</td>
                                                                            ))}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        
                        {/* --- Action Buttons --- */}
                        <div className="bg-gray-50/70 p-4 flex justify-end items-center space-x-4">
                            {step === 'preview' && !isLoading && (
                                <motion.div className="flex space-x-4" initial={{opacity: 0}} animate={{opacity: 1}}>
                                     <button
                                        onClick={handleGenerateWidget}
                                        className="font-semibold text-gray-600 hover:text-blue-600 px-4 py-2 rounded-md"
                                    >
                                        Retry
                                    </button>
                                    <motion.button
                                        onClick={handleAcceptWidget}
                                        className="px-6 py-2 bg-green-500 text-white font-bold rounded-lg shadow-md hover:bg-green-600"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        Accept and Add
                                    </motion.button>
                                </motion.div>
                            )}
                            <button onClick={handleClose} className="font-semibold text-gray-500 hover:text-gray-800 px-4 py-2">Cancel</button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default CreateGraphModal;